/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import isNil from 'lodash/isNil';

import ErrorLevel from '../../error/error-level';

/**
 * Transportér analytických dat.
 * Odesílá analytická data na Ki-Wi Server.
 */
class AnalyticsTransport {
  /**
   * Vygeneruje analytické počítadlo ve formátu pro server.
   * @param {AnalyticsCounter} analyticsCounter Analytické počítadlo k vygenerování.
   * @private
   * @returns {Object} Analytická data ve formátu pro server.
   */
  generateCounter(analyticsCounter) {
    // Sestavení obsahu a odfiltrování prázdných hodnot.
    return Object.fromEntries(
      Object.entries({
        count: analyticsCounter.played,
        // Posílání `name` místo `filename` je záměrné.
        filename: analyticsCounter.name,
        id: analyticsCounter.mediumId,
        mimetype: analyticsCounter.type
      }).filter(([key, value]) => !isNil(value))
    );
  }

  /**
   * Vygeneruje analytickou skupinu ve formátu pro server.
   * @param {Array.<AnalyticsCounter>} analyticsCounterArray Analytická počítadla k vygenerování.
   * @param {AnalyticsGroup} analyticsGroup Skupina analytických dat k vygenerování.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @private
   * @returns {Object} Analytická data ve formátu pro server.
   */
  generateGroup(analyticsCounterArray, analyticsGroup, deviceId) {
    // Sestavení obsahu a odfiltrování prázdných hodnot.
    return Object.fromEntries(
      Object.entries({
        deviceId,
        end: analyticsGroup.closed,
        media: analyticsCounterArray.map((analyticsCounter) =>
          this.generateCounter(analyticsCounter)
        ),
        start: analyticsGroup.opened
      }).filter(([key, value]) => !isNil(value))
    );
  }

  /**
   * Odešle analytická data na server.
   * @param {Array.<AnalyticsCounter>} analyticsCounterArray Analytická data pro média.
   * @param {AnalyticsGroup} analyticsGroup Skupina analytických dat.
   * @param {string} deviceId Identifikátor Ki-Wi zařízení.
   * @param {string} serverUrl URL adresa Ki-Wi Server pro komunikaci.
   * @returns {Promise.<undefined>} Nic.
   */
  send(analyticsCounterArray, analyticsGroup, deviceId, serverUrl) {
    return (
      fetch(`${serverUrl}/api/device/${deviceId}/analytics`, {
        body: JSON.stringify(
          this.generateGroup(analyticsCounterArray, analyticsGroup, deviceId)
        ),
        headers: { 'Content-type': 'application/json' },
        method: 'POST'
      })
        // Snížení priority logování pro chyby síťového spojení.
        .catch((error) => {
          error.level = ErrorLevel.Warning;
          throw error;
        })
        .then((response) => {
          // Snížení priority logování pro chyby ze serveru.
          if (!response.ok) {
            const error = new RangeError(
              `Invalid HTTP status "${response.status}".`
            );
            error.level = ErrorLevel.Warning;
            throw error;
          }
        })
    );
  }
}

export default AnalyticsTransport;
